import {
  addMonths,
  isValidIsoDate,
  ProjectionInput,
  projectionScenarioSchema,
  tryParseBirr,
  tryParseLiters,
  type ProjectionInputData,
  type TenantSettings,
} from '@dairy/shared';
import { projectionInputErrors } from '@dairy/domain';

export interface ProjectionFormValues {
  kind: ProjectionInputData['kind'];
  name: string;
  startDate: string;
  horizonMonths: 12 | 24 | 36;
  birthMode: 'DATE' | 'AGE';
  birthDate: string;
  birthDateEstimated: boolean;
  ageYears: string;
  ageMonths: string;
  breed: string;
  purchasePrice: string;
  endValue: string;
  parity: string;
  milking: boolean;
  litersNow: string;
  dimNow: string;
  lastPeak: string;
  expectedPeak: string;
  measuredByOwner: boolean;
  pregnant: boolean;
  pregnancyMode: 'DATE' | 'MONTHS';
  serviceDate: string;
  monthsPregnant: string;
}
export function newProjectionForm(today: string): ProjectionFormValues {
  return {
    kind: 'COW',
    name: '',
    startDate: today,
    horizonMonths: 12,
    birthMode: 'DATE',
    birthDate: '',
    birthDateEstimated: false,
    ageYears: '',
    ageMonths: '0',
    breed: '',
    purchasePrice: '',
    endValue: '',
    parity: '',
    milking: true,
    litersNow: '',
    dimNow: '',
    lastPeak: '',
    expectedPeak: '',
    measuredByOwner: false,
    pregnant: false,
    pregnancyMode: 'DATE',
    serviceDate: '',
    monthsPregnant: '1',
  };
}
export function changeProjectionKind(
  form: ProjectionFormValues,
  kind: ProjectionFormValues['kind'],
): ProjectionFormValues {
  if (form.kind === kind) return form;
  return {
    ...form,
    kind,
    horizonMonths: kind === 'COW' ? 12 : kind === 'HEIFER' ? 24 : 36,
    parity: '',
    milking: true,
    litersNow: '',
    dimNow: '',
    lastPeak: '',
    expectedPeak: '',
    measuredByOwner: false,
    pregnant: false,
    pregnancyMode: 'DATE',
    serviceDate: '',
    monthsPregnant: '1',
  };
}
const integer = (raw: string) =>
  /^\d+$/.test(raw.trim()) && Number.isSafeInteger(Number(raw)) ? Number(raw) : undefined;
export function parseProjectionForm(
  form: ProjectionFormValues,
  settings: TenantSettings,
): { name: string; input: ProjectionInputData | null; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const money = (key: 'purchasePrice' | 'endValue', required: boolean) => {
    const parsed = tryParseBirr(form[key]);
    if (!parsed.ok || (required && parsed.santim === null)) {
      errors[key] = key;
      return undefined;
    }
    return parsed.santim ?? undefined;
  };
  const liters = (key: 'litersNow' | 'lastPeak' | 'expectedPeak', required: boolean) => {
    const parsed = tryParseLiters(form[key]);
    if (!parsed.ok || (required && parsed.ml === null)) {
      errors[key] = key;
      return undefined;
    }
    return parsed.ml ?? undefined;
  };
  const name = projectionScenarioSchema.shape.name.safeParse(form.name);
  if (!name.success) errors.name = 'name';
  let birthDate = form.birthDate;
  if (form.birthMode === 'AGE') {
    const years = integer(form.ageYears),
      months = integer(form.ageMonths);
    if (
      years === undefined ||
      months === undefined ||
      months > 11 ||
      !Number.isSafeInteger(years * 12 + months) ||
      !isValidIsoDate(form.startDate)
    )
      errors.age = 'age';
    else {
      birthDate = addMonths(form.startDate, -(years * 12 + months));
      if (!isValidIsoDate(birthDate)) errors.age = 'age';
    }
  }
  const data: Record<string, unknown> = {
    kind: form.kind,
    startDate: form.startDate,
    horizonMonths: form.horizonMonths,
    birthDate,
    birthDateEstimated: form.birthMode === 'AGE' || form.birthDateEstimated,
    breed: form.breed.trim() || undefined,
    purchasePriceSantim: money('purchasePrice', true),
    endValueSantim: money('endValue', false),
  };
  if (form.kind === 'COW') {
    data.parity = integer(form.parity);
    data.milking = form.milking;
    if (form.milking) {
      data.litersNowMl = liters('litersNow', true);
      data.dimNow = integer(form.dimNow);
      data.measuredByOwner = form.measuredByOwner;
    } else data.lastPeakMl = liters('lastPeak', false);
  } else data.expectedPeakMl = liters('expectedPeak', false);
  if (form.kind !== 'CALF' && form.pregnant) {
    if (form.pregnancyMode === 'DATE') data.serviceDate = form.serviceDate;
    else {
      data.monthsPregnant = integer(form.monthsPregnant);
      if (data.monthsPregnant === undefined) errors.monthsPregnant = 'monthsPregnant';
    }
  }
  const parsed = ProjectionInput.safeParse(data);
  const aliases: Record<string, string> = {
    purchasePriceSantim: 'purchasePrice',
    endValueSantim: 'endValue',
    litersNowMl: 'litersNow',
    lastPeakMl: 'lastPeak',
    expectedPeakMl: 'expectedPeak',
  };
  if (!parsed.success)
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0]);
      errors[aliases[key] ?? key] = aliases[key] ?? key;
    }
  if (parsed.success)
    for (const field of projectionInputErrors(parsed.data, settings))
      errors[field] = field === 'kind' ? (form.kind === 'CALF' ? 'calfAge' : 'heiferAge') : 'calvingDate';
  return {
    name: name.success ? name.data : form.name.trim(),
    input: parsed.success && Object.keys(errors).length === 0 ? parsed.data : null,
    errors,
  };
}
